// §4.17 MERGE — the screen's pure rules (Module 09, P3-E2 PR E2-2b; spec 09
// §3.13 and §4.17, D-324 rulings 4 to 11). Every decision the screen makes
// about WHAT it sends lives here, as functions of the preview and the admin's
// choices, so each is unit-tested without a render and the screen only paints.
//
// THE FIELD CHOICES (§3.13's profile list, `CustomerMergeFieldChoices`). Every
// field starts on the winner, the customer in the address, because the screen
// is opened from that customer's 360 to keep it. Two rules bind the radios:
//  * THE CURRENCY SPLIT. The credit limit is counted in the default currency
//    and carries none of its own (§2.1, D-260), so when the two default
//    currencies differ, `default_currency` and `credit_limit_minor` always
//    come from one customer. Moving either radio moves the other, whether or
//    not the reader is shown the limit, because the server answers a split
//    choice 400 `merge_credit_currency_split` and a limit read at a rate of 1.0
//    is §9.13's forbidden case. When the currencies match the two are free.
//  * THE TIER. A tier that differs from the winner's needs `sales.tier.manage`
//    beside the admin lineage (the PATCH's two-layer rule), so for a reader
//    without the code the tier stays on the winner and its radio is fixed.
//    When the two tiers are equal there is nothing to change and the radio is
//    free, because either side sends the same value and the server admits it.
//
// THE ACCESS SET IS PART OF WHAT THE ADMIN APPROVES (§3.13, D-324 ruling 6).
// `add_collaborators` and `skip_collaborators` together name EXACTLY the
// preview's `gains_access`, split by the ticks, every user ticked until the
// admin clears one. A user the preview did not name is never sent, and a tick
// kept for a user a re-read preview no longer names is dropped, because the
// merge compares the two lists with the set it recomputes under its locks and
// answers `MERGE_STALE` for any difference.
//
// THE COMMERCIAL SET (§7 narrowing 19). A holder of `sales.read_value` is
// served the attachments by id and title and sends their ids back. Any other
// caller is served only `commercial_audience_withheld_count` and sends the
// empty list with `commercial_attachment_count` set to that count, which the
// merge compares with the size of the set it computes. The ids are never
// guessed from a count.
//
// WHAT CHANGED (§4.17's "MERGE_STALE reads the preview again and says what
// changed"). `staleChanges` compares the preview the admin confirmed with the
// one read after the refusal, on the four things the merge compares: each
// customer's version, the access set and the commercial set.

import type {
  MergeFieldChoices,
  MergePreview,
  MergeProfile,
  MergeRequest,
  MergeSide,
} from "../../hooks/merge-queries";

/** The profile fields, in the order the screen lists them (§3.13). */
export const MERGE_FIELDS = [
  "legal_name",
  "display_name",
  "tax_id",
  "country",
  "tier",
  "default_currency",
  "credit_limit_minor",
  "credit_terms_days",
  "credit_hold",
  "billing_address",
  "shipping_address",
  "preferred_channel",
  "preferred_locale",
] as const satisfies readonly (keyof MergeFieldChoices)[];

export type MergeField = (typeof MERGE_FIELDS)[number];

/** Every field on the winner: the screen's starting point (§4.17). */
export function defaultChoices(): MergeFieldChoices {
  return Object.fromEntries(MERGE_FIELDS.map((field) => [field, "winner"])) as MergeFieldChoices;
}

/** The facts the two binding rules read. */
export interface ChoiceRules {
  /** The two customers' default currencies differ. */
  readonly currenciesDiffer: boolean;
  /** The two customers' tiers differ. */
  readonly tiersDiffer: boolean;
  /** The reader holds `sales.tier.manage`. */
  readonly mayChangeTier: boolean;
}

export function choiceRules(preview: MergePreview, mayChangeTier: boolean): ChoiceRules {
  return {
    currenciesDiffer: preview.winner.default_currency !== preview.loser.default_currency,
    tiersDiffer: preview.winner.tier !== preview.loser.tier,
    mayChangeTier,
  };
}

/** Is this field's loser radio fixed, so the field stays on the winner? */
export function fieldLocked(field: MergeField, rules: ChoiceRules): boolean {
  return field === "tier" && rules.tiersDiffer && !rules.mayChangeTier;
}

/** The two fields one radio moves together while the currencies differ. */
const CURRENCY_PAIR: ReadonlySet<MergeField> = new Set(["default_currency", "credit_limit_minor"]);

/** Is this field one of the pair a currency difference binds? */
export function fieldLinked(field: MergeField, rules: ChoiceRules): boolean {
  return rules.currenciesDiffer && CURRENCY_PAIR.has(field);
}

/**
 * The choices after the admin picks `side` for `field`. A locked field does
 * not move. While the currencies differ, the currency and the limit move as
 * one, whichever of the two radios was pressed.
 */
export function chooseSide(
  choices: MergeFieldChoices,
  field: MergeField,
  side: MergeSide,
  rules: ChoiceRules,
): MergeFieldChoices {
  if (fieldLocked(field, rules) && side === "loser") return choices;
  const next: MergeFieldChoices = { ...choices, [field]: side };
  if (fieldLinked(field, rules)) {
    next.default_currency = side;
    next.credit_limit_minor = side;
  }
  return next;
}

/**
 * Bring choices made against one preview into line with another (a re-read
 * after `MERGE_STALE`). A choice the rules still allow is kept as the admin
 * set it; a tier the reader may no longer take returns to the winner, and a
 * currency pair a new currency difference splits follows the currency.
 */
export function reconcileChoices(
  choices: MergeFieldChoices,
  rules: ChoiceRules,
): MergeFieldChoices {
  const next: MergeFieldChoices = { ...choices };
  if (fieldLocked("tier", rules)) next.tier = "winner";
  if (rules.currenciesDiffer) next.credit_limit_minor = next.default_currency;
  return next;
}

/** The ticks for a preview's `gains_access`: everyone ticked except the users
 * the admin cleared, and nobody the preview does not name. */
export function reconcileTicks(
  preview: MergePreview,
  cleared: ReadonlySet<string>,
): ReadonlySet<string> {
  const names = new Set(preview.gains_access.map((grant) => grant.user_id));
  return new Set([...cleared].filter((userId) => names.has(userId)));
}

/** The merge body for this preview and these choices (§5's E2-2 row 16). */
export function mergeBody(
  preview: MergePreview,
  choices: MergeFieldChoices,
  cleared: ReadonlySet<string>,
): MergeRequest {
  const add: string[] = [];
  const skip: string[] = [];
  for (const grant of preview.gains_access) {
    (cleared.has(grant.user_id) ? skip : add).push(grant.user_id);
  }
  const listed = preview.commercial_audience_changes;
  return {
    loser_id: preview.loser.id,
    fields: { ...choices },
    winner_version: preview.winner_version,
    loser_version: preview.loser_version,
    add_collaborators: add,
    skip_collaborators: skip,
    commercial_attachment_ids: listed === undefined ? [] : listed.map((file) => file.id),
    ...(listed === undefined
      ? { commercial_attachment_count: preview.commercial_audience_withheld_count ?? 0 }
      : {}),
  };
}

/** May Confirm be pressed (§4.17)? Never while a blocker stands, offline,
 * or while a merge is already in flight. */
export function confirmAllowed(preview: MergePreview, offline: boolean, busy: boolean): boolean {
  return preview.blockers.length === 0 && !offline && !busy;
}

/** What a re-read preview changed, in the four facts the merge compares. */
export type StaleChange = "winner" | "loser" | "access" | "files";

export function staleChanges(before: MergePreview, after: MergePreview): StaleChange[] {
  const out: StaleChange[] = [];
  if (before.winner_version !== after.winner_version) out.push("winner");
  if (before.loser_version !== after.loser_version) out.push("loser");
  const people = (preview: MergePreview) =>
    preview.gains_access
      .map((grant) => grant.user_id)
      .sort()
      .join(",");
  if (people(before) !== people(after)) out.push("access");
  const files = (preview: MergePreview) =>
    preview.commercial_audience_changes === undefined
      ? `count:${String(preview.commercial_audience_withheld_count ?? 0)}`
      : preview.commercial_audience_changes
          .map((file) => file.id)
          .sort()
          .join(",");
  if (files(before) !== files(after)) out.push("files");
  return out;
}

/**
 * The kinds of record the merge reports, in the order the screen lists them:
 * each table that moves (§2.33's move set, `CustomerMergeMoveCounts`) and the
 * reliability scores, which only stay. A kind that also stays carries the
 * reason its rows stay (§3.13). The move set is the contract's
 * `customerMergeMoveTables`, which the test holds this list to, so a table the
 * merge learns to move is a row here. E2-2a2's `sales_attachments` was served
 * by the preview and missing from this list, so the screen showed no line for
 * the customer's attached files that were about to move (M09-S29 on the
 * local replica, 2026-10-04).
 */
export const MERGE_KINDS = [
  "customer_contacts",
  "contact_channels",
  "customer_notes",
  "customer_tag_links",
  "quotes",
  "sales_orders",
  "opportunities",
  "sales_activities",
  "customer_notifications",
  "customer_price_overrides",
  "customer_distributor_products",
  "customer_assignments",
  "sales_attachments",
  "quote_sends",
  "customer_reliability_snapshots",
] as const;

export type MergeKind = (typeof MERGE_KINDS)[number];

export interface MergeKindRow {
  readonly kind: MergeKind;
  /** Rows that move to the winner; undefined for a kind that never moves. */
  readonly moves: number | undefined;
  /** Rows that stay on the loser; undefined for a kind that never stays. */
  readonly stays: number | undefined;
}

export function kindRows(preview: MergePreview): MergeKindRow[] {
  const moves = preview.moves as Partial<Record<MergeKind, number>>;
  const stays = preview.stays as Partial<Record<MergeKind, number>>;
  return MERGE_KINDS.map((kind) => ({ kind, moves: moves[kind], stays: stays[kind] }));
}

/** One line for an address, from its own parts in reading order, or `""`
 * when nothing is recorded (§2.1's `SalesAddress`). */
export function addressLine(address: MergeProfile["billing_address"]): string {
  return [
    address.line1,
    address.line2,
    address.city,
    address.region,
    address.postal,
    address.country,
  ]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter((part) => part.length > 0)
    .join(", ");
}
